import { useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiPlus, FiTag } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
  useCreateSaving,
  useDeleteSaving,
  useSavings,
  useSavingsFunds,
  useUpdateSaving,
  type SavingPlanBalance,
} from "@/hooks/useSavings";
import { useCreateTransactionFromForm, type TransactionFormData } from "@/hooks/useCreateTransactionFromForm";
import { formatCurrency, formatDate, formatPercent } from "@/utils/formatters";
import { getApiErrorMessage } from "@/utils/apiError";
import { NewSavingModal } from "../components/NewSavingModal";
import { NewTransactionModal } from "@/domains/transactions/components/NewTransactionModal";
import { GoalFeatureCard } from "../components/GoalFeatureCard";
import { GoalRow } from "../components/GoalRow";
import { confirmAction, showAppMessage } from "@/components/ui/confirmDialog";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { DomainSidebar, DomainSidebarHeader, DomainTwoColumnContent, DomainTwoColumnLayout } from "@/components/ui/DomainSidebar";
import { useNavigationState } from "@/hooks/useNavigationState";
import { SearchInput } from "@/components/ui/SearchInput";
import "../savings.css";

interface SelectedSaving {
  id: number;
  name: string;
  description?: string;
  initial_amount: number;
  target_amount: number;
  expected_date?: string;
  expected_monthly_payment?: number;
}

type StatusFilter = "all" | "in-progress" | "completed";
type GoalSort = "due-asc" | "due-desc" | "progress-desc" | "name-asc" | "name-desc";


const getProgressPercentage = (current: number, target: number) =>
  target > 0 ? Math.min((current / target) * 100, 100) : 0;

const isGoalCompleted = (plan: SavingPlanBalance) => plan.saved_amount >= plan.target_amount;

const getRemainingAmount = (plan: SavingPlanBalance) => Math.max(0, plan.target_amount - plan.saved_amount);

const getDueTime = (targetDate?: string) => {
  if (!targetDate) return null;
  const time = new Date(`${targetDate}T00:00:00`).getTime();
  return Number.isNaN(time) ? null : time;
};

/** Goals without a target date always go last, whichever direction is chosen. */
const compareDue = (a: SavingPlanBalance, b: SavingPlanBalance, direction: 1 | -1) => {
  const aTime = getDueTime(a.target_date);
  const bTime = getDueTime(b.target_date);
  if (aTime === null && bTime === null) return 0;
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  return direction * (aTime - bTime);
};

const compareGoals = (a: SavingPlanBalance, b: SavingPlanBalance, sortBy: GoalSort) => {
  switch (sortBy) {
    case "name-asc":
      return a.name.localeCompare(b.name);
    case "name-desc":
      return b.name.localeCompare(a.name);
    case "progress-desc":
      return (
        getProgressPercentage(b.saved_amount, b.target_amount) -
        getProgressPercentage(a.saved_amount, a.target_amount)
      );
    case "due-desc":
      return compareDue(a, b, -1);
    default:
      return compareDue(a, b, 1);
  }
};

/** Whole pesos in goal lines; the aside keeps the cents of the totals. */
const formatGoalAmount = (value: number) =>
  formatCurrency(value, undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const SavingsView = () => {
  const { t } = useTranslation(["savings", "common"]);
  const { data: savings, isLoading, error, refetch: refetchSavings } = useSavings();
  const { data: savingsFunds = [], error: savingsFundsError } = useSavingsFunds();
  const updateSavingMutation = useUpdateSaving();
  const createSavingMutation = useCreateSaving();
  const deleteSavingMutation = useDeleteSaving();
  const createTransactionFromForm = useCreateTransactionFromForm();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSaving, setSelectedSaving] = useState<SelectedSaving | null>(null);
  const [contributionGoal, setContributionGoal] = useState<SavingPlanBalance | null>(null);
  const [searchTerm, setSearchTerm] = useNavigationState("savings.searchTerm", "");
  const [statusFilter, setStatusFilter] = useNavigationState<StatusFilter>("savings.status", "all");
  const [sortBy, setSortBy] = useNavigationState<GoalSort>("savings.sort", "due-asc");

  const totalSaved = savings?.total_saved ?? 0;
  const monthlyExpected = savings?.monthly_expected ?? 0;
  const savingPlans = useMemo(() => savings?.saving_plans_balance ?? [], [savings?.saving_plans_balance]);
  const savingDescriptionById = useMemo(
    () => new Map(savingsFunds.map((saving) => [saving.id, saving.description?.trim() ?? ""])),
    [savingsFunds],
  );

  const activeSavingPlans = useMemo(
    () =>
      savingPlans
        .filter((plan) => !isGoalCompleted(plan))
        .sort((a, b) => getProgressPercentage(b.saved_amount, b.target_amount) - getProgressPercentage(a.saved_amount, a.target_amount)),
    [savingPlans],
  );
  const completedCount = savingPlans.length - activeSavingPlans.length;
  const remainingForGoals = useMemo(
    () => activeSavingPlans.reduce((sum, plan) => sum + getRemainingAmount(plan), 0),
    [activeSavingPlans],
  );

  const visibleGoals = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();
    return savingPlans
      .filter((plan) => {
        const completed = isGoalCompleted(plan);
        if (statusFilter === "in-progress" && completed) return false;
        if (statusFilter === "completed" && !completed) return false;
        if (!query) return true;
        const description = savingDescriptionById.get(plan.id) ?? "";
        return [plan.name, description].some((value) => value.toLocaleLowerCase().includes(query));
      })
      .sort((a, b) => compareGoals(a, b, sortBy));
  }, [savingDescriptionById, savingPlans, searchTerm, sortBy, statusFilter]);

  // Feature card: first goal in progress after filters and sort; falls back to the first visible goal.
  const featuredGoal = visibleGoals.find((plan) => !isGoalCompleted(plan)) ?? visibleGoals[0] ?? null;
  const listedGoals = visibleGoals.filter((plan) => plan.id !== featuredGoal?.id);
  const visibleInProgress = visibleGoals.filter((plan) => !isGoalCompleted(plan)).length;
  const visibleCompleted = visibleGoals.length - visibleInProgress;

  const insightGoal = activeSavingPlans[0] ?? null;

  const primaryInsight = useMemo(() => {
    if (!insightGoal) {
      return completedCount > 0 ? t("savings.insightAllDone") : t("savings.noTargetsYet");
    }

    const percentage = getProgressPercentage(insightGoal.saved_amount, insightGoal.target_amount);
    const remaining = getRemainingAmount(insightGoal);
    const remainingPercent = Math.max(0, 100 - percentage);
    const insightName = /japan|japón/i.test(insightGoal.name) ? t("savings.insightJapanTrip") : insightGoal.name;

    if (percentage >= 75) {
      return t("savings.insightNearlyDone", {
        name: insightName,
        percent: `${formatPercent(remainingPercent, undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`,
      });
    }

    return t("savings.insightRemaining", {
      name: insightName,
      amount: formatCurrency(remaining),
    });
  }, [completedCount, insightGoal, t]);

  // The transaction modal only reads initialData when it opens; keep the reference stable.
  const contributionInitialData = useMemo(
    () => (contributionGoal ? { saving_plan_id: contributionGoal.id } : undefined),
    [contributionGoal],
  );

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: "all", label: t("savings.statusFilterAll") },
    { value: "in-progress", label: t("savings.statusFilterInProgress") },
    { value: "completed", label: t("savings.statusFilterCompleted") },
  ];
  const sortOptions: { value: GoalSort; label: string }[] = [
    { value: "due-asc", label: t("savings.sortDueAsc") },
    { value: "due-desc", label: t("savings.sortDueDesc") },
    { value: "progress-desc", label: t("savings.sortProgressDesc") },
    { value: "name-asc", label: t("savings.sortNameAsc") },
    { value: "name-desc", label: t("savings.sortNameDesc") },
  ];
  const statusLabel = statusOptions.find((option) => option.value === statusFilter)?.label ?? "";
  const sortLabel = sortOptions.find((option) => option.value === sortBy)?.label ?? "";

  const handleAddSaving = () => {
    setSelectedSaving(null);
    setIsEditModalOpen(true);
  };

  const openSavingEditor = (plan: SavingPlanBalance) => {
    setSelectedSaving({
      id: plan.id,
      name: plan.name,
      description: savingDescriptionById.get(plan.id) || "",
      initial_amount: plan.initial_amount ?? plan.saved_amount,
      target_amount: plan.target_amount,
      expected_date: plan.target_date,
      expected_monthly_payment: plan.expected_monthly_payment,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveSaving = async (
    savingData: {
      name: string;
      description?: string;
      initial_amount: number;
      target_amount: number;
      expected_date?: string;
    },
    savingId?: number,
  ) => {
    try {
      if (savingId) {
        await updateSavingMutation.mutateAsync({ id: savingId, data: savingData });
      } else {
        await createSavingMutation.mutateAsync(savingData);
      }
      setIsEditModalOpen(false);
      setSelectedSaving(null);
    } catch {
      // Mutations surface user-facing errors upstream.
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedSaving(null);
  };

  const handleDeleteSaving = async (plan: SavingPlanBalance) => {
    const confirmed = await confirmAction({
      title: t("common.prompts.deleteSaving"),
      body: plan.name,
      confirmLabel: t("common.actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteSavingMutation.mutateAsync(plan.id);
    } catch {
      showAppMessage({ title: t("common.errors.deleteSaving"), tone: "danger" });
    }
  };

  const handleContributeTransaction = async (transactionData: TransactionFormData) => {
    try {
      await createTransactionFromForm(transactionData);
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, t("common.errors.createMovement"));
      showAppMessage({ title: message, tone: "danger" });
      // Rethrow so the modal stays open with the entered data.
      throw error instanceof Error ? error : new Error(message);
    }
  };

  const formatDueLabel = (targetDate?: string) =>
    targetDate
      ? t("savings.dueDate", {
          date: formatDate(new Date(`${targetDate}T00:00:00`), undefined, { day: "numeric", month: "short" }),
        })
      : t("savings.noTargetDate");

  const renderFeaturedGoal = (plan: SavingPlanBalance) => {
    const percent = getProgressPercentage(plan.saved_amount, plan.target_amount);
    const completed = isGoalCompleted(plan);

    return (
      <GoalFeatureCard
        name={plan.name}
        amountLine={
          completed
            ? t("savings.goalAmountLine", { saved: formatGoalAmount(plan.saved_amount), target: formatGoalAmount(plan.target_amount) })
            : t("savings.goalAmountMissingLine", {
                saved: formatGoalAmount(plan.saved_amount),
                target: formatGoalAmount(plan.target_amount),
                missing: formatGoalAmount(getRemainingAmount(plan)),
              })
        }
        percent={percent}
        percentLabel={`${Math.round(percent)}%`}
        percentCaption={t("savings.achievedCaption")}
        kicker={t("savings.primaryGoal")}
        dueLabel={formatDueLabel(plan.target_date)}
        contributeLabel={t("savings.contribute")}
        onContribute={completed ? undefined : () => setContributionGoal(plan)}
        menuLabel={t("savings.goalActionsTitle", { name: plan.name })}
        editLabel={`${t("common:actions.edit")} ${plan.name}`}
        deleteLabel={`${t("common:actions.delete")} ${plan.name}`}
        onEdit={() => openSavingEditor(plan)}
        onDelete={() => handleDeleteSaving(plan)}
      />
    );
  };

  const renderGoalRow = (plan: SavingPlanBalance) => {
    const percent = getProgressPercentage(plan.saved_amount, plan.target_amount);
    const completed = isGoalCompleted(plan);

    return (
      <GoalRow
        key={plan.id}
        name={plan.name}
        amountLine={t("savings.goalAmountLine", {
          saved: formatGoalAmount(plan.saved_amount),
          target: formatGoalAmount(plan.target_amount),
        })}
        detailLine={formatDueLabel(plan.target_date)}
        percent={percent}
        percentLabel={`${Math.round(percent)}%`}
        completed={completed}
        completedLabel={t("savings.statusCompleted")}
        menuLabel={t("savings.goalActionsTitle", { name: plan.name })}
        editLabel={`${t("common:actions.edit")} ${plan.name}`}
        deleteLabel={`${t("common:actions.delete")} ${plan.name}`}
        onEdit={() => openSavingEditor(plan)}
        onDelete={() => handleDeleteSaving(plan)}
        onContribute={completed ? undefined : () => setContributionGoal(plan)}
        contributeLabel={completed ? undefined : t("savings.contribute")}
      />
    );
  };

  useEffect(() => {
    void refetchSavings();
  }, [refetchSavings]);

  if (isLoading) {
    return <PageSkeleton lines={4} />;
  }

  if (error || savingsFundsError) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] px-8 py-6 text-center">
          <p className="font-medium text-[var(--app-text)]">{t("savings.loadError")}</p>
        </div>
      </div>
    );
  }

  const hasGoals = savingPlans.length > 0;

  return (
    <main className="app-page savings-page">
      <div className="app-container max-w-none !overflow-visible">
        <div className="relative mx-auto w-full max-w-2xl min-w-0 md:max-w-none">
          <DomainTwoColumnLayout className="!h-auto !overflow-visible">
            <DomainSidebar className="lg:sticky lg:top-32 lg:h-fit lg:overflow-visible">
              <DomainSidebarHeader greeting={t("common:labels.greeting")} title={t("common:nav.savings")} />

              <section className="app-domain-sidebar-section">
                <p className="savings-hero-label">
                  {hasGoals
                    ? t("savings.totalSavedKicker", { goals: t("savings.completedGoals", { count: completedCount }) })
                    : t("savings.completedGoals", { count: completedCount })}
                </p>
                <p className="app-domain-sidebar-value">{formatCurrency(totalSaved)}</p>
              </section>

              {hasGoals ? (
                <section className="app-domain-sidebar-section">
                  <dl className="savings-facts">
                    <div className="savings-facts__row">
                      <dt>{t("savings.remainingForGoals")}</dt>
                      <dd>{formatCurrency(remainingForGoals)}</dd>
                    </div>
                    <div className="savings-facts__row">
                      <dt>{t("savings.monthlyContributionNeeded")}</dt>
                      <dd>{formatCurrency(monthlyExpected)}</dd>
                    </div>
                  </dl>
                </section>
              ) : null}
            </DomainSidebar>

            <DomainTwoColumnContent className="!h-auto !overflow-visible pb-20">
              {!hasGoals ? (
                <section className="pb-6">
                  <div className="savings-empty">
                    <div className="savings-empty__tile" aria-hidden="true">
                      <img src="/brand/jerry-face-mark-ink.svg" alt="" className="h-9 w-9 object-contain" />
                    </div>
                    <h3>{t("savings.emptyStateTitle")}</h3>
                    <p>{t("savings.emptyStateNewBody")}</p>
                    <button type="button" onClick={handleAddSaving} className="savings-flame app-focus-ring">
                      <FiPlus aria-hidden="true" className="h-4 w-4" />
                      {t("savings.createGoalAction")}
                    </button>
                  </div>
                </section>
              ) : (
                <section className="pb-8">
                  <div className="savings-toolbar flex flex-col gap-3 xl:flex-row xl:items-center" role="toolbar" aria-label={t("savings.filterToolbarLabel")}>
                    <SearchInput
                      className="min-w-0 flex-1"
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder={t("savings.searchGoal")}
                      label={t("savings.searchGoal")}
                    />
                    <label className="savings-pill">
                      <FiTag className="h-[18px] w-[18px]" aria-hidden="true" />
                      <span>{t("savings.filterStatusLabel", { value: statusLabel })}</span>
                      <FiChevronDown className="h-4 w-4" aria-hidden="true" />
                      <select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                        aria-label={t("savings.filterStatusAria")}
                        className="app-focus-ring savings-pill__select"
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="savings-pill">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-[18px] w-[18px]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.75}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M7 4v16M4 17l3 3 3-3M17 20V4M14 7l3-3 3 3" />
                      </svg>
                      <span>{sortLabel}</span>
                      <select
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value as GoalSort)}
                        aria-label={t("savings.sortByLabel")}
                        className="app-focus-ring savings-pill__select"
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="savings-list-title">
                    <h2>{t("savings.activeGoalsTitle")}</h2>
                    <span>
                      {t("savings.listSummary", {
                        inProgress: t("savings.inProgressCount", { count: visibleInProgress }),
                        completed: t("savings.completedShort", { count: visibleCompleted }),
                      })}
                    </span>
                  </div>

                  {visibleGoals.length === 0 ? (
                    <p className="py-10 text-center text-sm text-[var(--ds-ink-muted)]">
                      {t("common.messages.noResults", { defaultValue: "No se encontraron resultados." })}
                    </p>
                  ) : (
                    <div className="savings-goals">
                      {featuredGoal ? renderFeaturedGoal(featuredGoal) : null}
                      <div className="savings-goals__list">{listedGoals.map((plan) => renderGoalRow(plan))}</div>
                    </div>
                  )}

                  <aside className="savings-insight" aria-label={t("savings.insightLabel")}>
                      <div className="savings-insight__who">
                        <img src="/brand/jerry-face-mark-ink.svg" alt="" aria-hidden="true" className="h-8 w-8 object-contain" />
                        {t("savings.insightLabel")}
                      </div>
                      <p className="savings-insight__title">{primaryInsight}</p>
                      {insightGoal ? (
                        <button type="button" onClick={() => openSavingEditor(insightGoal)} className="savings-insight__action app-focus-ring">
                          {t("savings.adjustContribution")}
                        </button>
                      ) : null}
                  </aside>
                </section>
              )}
            </DomainTwoColumnContent>
          </DomainTwoColumnLayout>
        </div>

        <NewSavingModal
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSaveSaving}
          saving={selectedSaving}
        />

        <NewTransactionModal
          isOpen={contributionGoal !== null}
          onClose={() => setContributionGoal(null)}
          onSubmit={handleContributeTransaction}
          initialData={contributionInitialData}
        />
      </div>
    </main>
  );
};
